/* ==========================================================================
   <cart-page>
   Full cart page. Mutates the cart and re-renders its body from server-rendered
   section HTML (cart AJAX `sections` param) — same approach as <cart-drawer>, so
   the {% form 'cart' %}, express-checkout buttons, localized URLs, | money
   formatting, line discounts and free-shipping progress stay intact after every
   quantity/remove without a full page reload.
   Click listeners are scoped to this element, so they never collide with the
   drawer's identical data-cart-* attributes.
   ========================================================================== */

function cartPageStorefrontRoot() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
    || (window.theme && window.theme.routes && window.theme.routes.root)
    || '/';
}

class CartPage extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-cart-page-body]');
    this.count = document.querySelector('[data-cart-count]');
    this.sectionId = this.dataset.sectionId; // JSON-template section id, e.g. "template--123__main"
    this._chain = Promise.resolve(); // serialize mutations to avoid lost updates
    this._setupStickyCheckout();
    this._walletObserver = window.EssentialsVault?.cartCheckoutUi?.watch(this) || null;

    this.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) {
        e.preventDefault();
        this._enqueue(() => this._change(removeBtn.dataset.lineKey, 0));
        return;
      }
      const rmDiscount = e.target.closest('[data-discount-remove]');
      if (rmDiscount) {
        e.preventDefault();
        this._enqueue(() => this._removeDiscount());
        return;
      }
      const qtyBtn = e.target.closest('[data-cart-qty]');
      if (qtyBtn) {
        e.preventDefault();
        const key = qtyBtn.dataset.lineKey;
        const delta = parseInt(qtyBtn.dataset.delta, 10);
        this._enqueue(() => this._changeBy(key, delta));
      }
    });

    this.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-discount-form]');
      if (!form) return;
      e.preventDefault();
      this._enqueue(() => this._applyDiscount(form));
    });
  }

  disconnectedCallback() {
    if (this._stickyObserver) this._stickyObserver.disconnect();
    if (this._walletObserver) this._walletObserver.disconnect();
  }

  _setupStickyCheckout() {
    if (this._stickyObserver) this._stickyObserver.disconnect();
    const primary = this.querySelector('[data-cart-primary-checkout]');
    const sticky = this.querySelector('[data-cart-sticky]');
    if (!primary || !sticky) return;

    // Fail closed: without IntersectionObserver the ordinary checkout remains
    // available and the page never renders two identical primary actions.
    sticky.hidden = true;
    if (typeof window.IntersectionObserver !== 'function') return;
    this._stickyObserver = new window.IntersectionObserver((entries) => {
      const entry = entries[0];
      if (entry) sticky.hidden = entry.isIntersecting;
    });
    this._stickyObserver.observe(primary);
  }

  // Chain cart mutations so a rapid double-click can't read-modify-write a
  // stale quantity (each task runs after the previous one has re-rendered).
  _enqueue(task) {
    this._chain = this._chain.then(task).catch((err) => console.error(err));
    return this._chain;
  }

  async _changeBy(key, delta) {
    if (!key) return;
    const cart = await (await fetch(`${cartPageStorefrontRoot()}cart.js`, { headers: { Accept: 'application/json' } })).json();
    const line = cart.items.find((i) => i.key === key);
    if (!line) return;
    await this._change(key, Math.max(0, line.quantity + delta));
  }

  async _change(key, quantity) {
    if (!key) return;
    this.setAttribute('data-busy', 'true');
    try {
      const res = await fetch(`${cartPageStorefrontRoot()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ id: key, quantity, sections: this.sectionId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error('Cart update failed');
      await this._render(data.sections);
      this._refreshSibling('cart-drawer');
    } finally {
      this.removeAttribute('data-busy');
    }
  }

  async _applyDiscount(form) {
    const helper = window.EssentialsVault && window.EssentialsVault.cartDiscount;
    if (!helper) return;
    const box = form.closest('[data-cart-discount]');
    const input = form.querySelector('[data-discount-input]');
    const code = input ? input.value.trim() : '';
    if (!code) return;
    if (box) box.setAttribute('data-busy', 'true');
    try {
      const { applicable } = await helper.apply(code);
      if (!applicable) {
        await helper.remove(); // drop the rejected code so it can't reach checkout
        this._showDiscountError(box);
        return;
      }
      await this._render();
      this._refreshSibling('cart-drawer'); // the drawer shares this cart — keep it in sync
    } catch (err) {
      this._showDiscountError(box);
    } finally {
      if (box) box.removeAttribute('data-busy');
    }
  }

  async _removeDiscount() {
    const helper = window.EssentialsVault && window.EssentialsVault.cartDiscount;
    if (!helper) return;
    await helper.remove();
    await this._render();
    this._refreshSibling('cart-drawer');
  }

  _showDiscountError(box) {
    const el = box && box.querySelector('[data-discount-error]');
    if (!el) return;
    if (el.dataset.msg) el.textContent = el.dataset.msg;
    el.hidden = false;
  }

  // A discount changes the whole cart, so the other cart UI (the always-present
  // drawer on the cart page) must re-render too or it shows a stale total/chip.
  _refreshSibling(selector) {
    const el = document.querySelector(selector);
    if (el && typeof el.refresh === 'function') el.refresh();
  }

  // Public: re-render this cart page's body from the server. Called by the
  // drawer after it mutates the shared cart (discount apply/remove).
  async refresh() {
    return this._enqueue(() => this._render());
  }

  async _render(sections) {
    let html = sections && sections[this.sectionId];
    if (!html) {
      // Fallback: Section Rendering API.
      html = await (await fetch(`${window.location.pathname}?section_id=${this.sectionId}`)).text();
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newBody = doc.querySelector('[data-cart-page-body]');
    if (newBody && this.body) this.body.innerHTML = newBody.innerHTML;
    this._syncCount();
    this._setupStickyCheckout();
    window.EssentialsVault?.cartCheckoutUi?.sync(this);
    this._reinitPaymentButtons();
  }

  _syncCount() {
    if (!this.count) return;
    const src = this.querySelector('[data-cart-count-source]');
    const n = src ? parseInt(src.textContent, 10) : NaN;
    if (Number.isNaN(n)) return;
    this.count.textContent = n;
    this.count.toggleAttribute('hidden', n === 0);
  }

  // Shopify wires dynamic checkout buttons (Shop Pay / Apple Pay / …) on load;
  // after we swap innerHTML the fresh markup needs re-initialising.
  _reinitPaymentButtons() {
    try {
      if (window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') {
        window.Shopify.PaymentButton.init();
      }
    } catch (err) {
      /* non-fatal */
    }
  }
}

if (!customElements.get('cart-page')) {
  customElements.define('cart-page', CartPage);
}
